// The single-use feedback link from an SMS (docs/plans/phase3-portal-feedback-kiswahili.md
// §5.1). The token is in this page's address because the SMS has to carry it; the page posts
// it in the request body, never in an API address. It reveals only the facility and the
// visit date, which the patient already knows. Referrer and caching are off for the whole
// app (index.html and the API's headers).
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice } from '@wodi/ui';
import type { SubmitFeedbackBody } from '@wodi/shared';
import { useState } from 'react';

import { formatEatDate } from '@wodi/domain';

import { ApiError } from '../api/client.ts';
import { openFeedbackLink, submitFeedbackLink } from '../api/portal.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, useI18n } from '../i18n/i18n.tsx';
import { FeedbackForm } from './feedback-form.tsx';
import { PortalFrame } from './frame.tsx';
import styles from './portal.module.css';

const route = getRouteApi('/f/$token');

export function FeedbackLinkPage() {
  const { token } = route.useParams();
  const { t, language } = useI18n();
  const online = useOnline();
  const [sent, setSent] = useState(false);
  const link = useQuery({
    queryKey: ['feedback-link'],
    queryFn: () => openFeedbackLink(token),
    retry: false,
    staleTime: Infinity,
  });
  const submit = useMutation({
    mutationFn: (body: SubmitFeedbackBody) => submitFeedbackLink(token, body),
    onSuccess: () => {
      setSent(true);
    },
  });

  const gone =
    link.error instanceof ApiError && (link.error.status === 404 || link.error.status === 400);
  return (
    <PortalFrame organisationName={link.data?.facilityName ?? t('feedback.link.title')}>
      <section className={styles.card} aria-labelledby="feedback-link">
        <h1 id="feedback-link" className={styles.title}>
          {t('feedback.link.title')}
        </h1>
        {link.isPending ? <LoadingBlock label={t('feedback.link.loading')} rows={2} /> : null}
        {link.isError ? (
          gone ? (
            <Notice tone="info">{t('feedback.link.gone')}</Notice>
          ) : (
            <Notice
              tone="error"
              action={
                <Button dense isDisabled={!online} onPress={() => void link.refetch()}>
                  {t('common.tryAgain')}
                </Button>
              }
            >
              {online ? errorText(t, language, link.error) : t('errors.offline')}
            </Notice>
          )
        ) : null}
        {link.data === undefined ? null : sent ? (
          <Notice tone="info">{t('feedback.thanks')}</Notice>
        ) : (
          <>
            <p className={styles.lead}>
              {t('feedback.link.about', {
                facility: link.data.facilityName,
                date: formatEatDate(new Date(`${link.data.visitDate}T09:00:00Z`)),
              })}
            </p>
            <FeedbackForm
              isPending={submit.isPending}
              error={submit.error === null ? null : errorText(t, language, submit.error)}
              onSubmit={(body) => {
                submit.mutate(body);
              }}
            />
          </>
        )}
      </section>
    </PortalFrame>
  );
}
